import { Icon } from "@iconify/react/dist/iconify.js";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import CustomSelect from "./ui/CustomSelect";
import { TextWrap } from "./ui/TextWrap";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";

const BranchLocations = () => {
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [loading, setLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const navigate = useNavigate();

  const fetchClients = async () => {
    try {
      setClientsLoading(true);
      const endpoint = `/get-all-clients?page=1&per_page=1000&search=&sort_by=client_id&sort_order=desc`;
      const response = await apiCall("get", endpoint, null, navigate);

      if (response?.data?.success) {
        const clientList = response.data.data || [];
        setClients(clientList);
      } else {
        console.error("Failed to load clients.");
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setClientsLoading(false);
    }
  };

  const fetchLocations = async (
    page = 1,
    perPage = 10,
    sortBy = null,
    sortOrder = "asc",
    search = "",
    clientId = ""
  ) => {
    try {
      setLoading(true);
      setErrorMessage("");

      let endpoint = `/get-all-branch-locations-bo?page=${page}&per_page=${perPage}`;

      if (sortBy) {
        endpoint += `&sort_by=${sortBy}&sort_order=${sortOrder}`;
      }

      if (search) {
        endpoint += `&search=${encodeURIComponent(search)}`;
      }

      if (clientId) {
        endpoint += `&client_id=${encodeURIComponent(clientId)}`;
      }

      const response = await apiCall("get", endpoint, null, {
        headers: {
          Authorization: `Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJodHRwOi8vcGV2YmFja2VuZC5jb2RvdmlvLmNvbS9wdWJsaWMiLCJzdWIiOjMsImlhdCI6MTc1MDE3MTIzMiwiZXhwIjoxNzUwMjU3NjMyLCJqdGkiOiI6ODUxN2U2MGFjMTk1In0.OApyVXzy1so-ew317nYqRbq3MIYZLzNqxTXv0jcvJBs`,
        },
      });

      if (response?.data?.success) {
        const locationList = response.data.data || [];
        setLocations(locationList);
        setFilteredLocations(locationList);

        setTotalEntries(
          response.data.pagination?.total || response.data.total || 0
        );
        setTotalPages(
          response.data.pagination?.last_page || response.data.last_page || 1
        );
      } else {
        setErrorMessage("Failed to load locations.");
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      setErrorMessage(error.message || "Error fetching locations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []); // Sirf ek baar chalega jab component mount hoga

  useEffect(() => {
    fetchLocations(
      currentPage,
      entriesPerPage,
      sortConfig.field,
      sortConfig.direction,
      searchTerm,
      selectedClient
    );
  }, [
    currentPage,
    entriesPerPage,
    sortConfig.field,
    sortConfig.direction,
    searchTerm,
    selectedClient,
  ]);

  const handleEntriesPerPageChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handleClientChange = (selected) => {
    setSelectedClient(selected?.value || "");
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1)
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      else if (endPage === totalPages)
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = Math.min(
    startIndex + entriesPerPage,
    filteredLocations.length
  );
  const currentLocations = filteredLocations;

  if (errorMessage) {
    return <ErrorAlert />;
  }
  return (
    <div className="p-4 p-md-3 p-sm-2">
      <div className="card shadow-sm">
        <div className="card p-3 p-sm-2 p-md-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div
              className="icon-field flex-grow-1"
              style={{ maxWidth: "400px" }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <PerPageDropdown
              perPage={entriesPerPage}
              handlePerPageChange={handleEntriesPerPageChange}
            />
            <div style={{ minWidth: "200px" }}>
              <CustomSelect
                name="client"
                value={selectedClient}
                onChange={handleClientChange}
                options={clients}
                placeholder="Select Client"
                labelKey="client_name"
                valueKey="client_id"
                isLoading={clientsLoading}
                isClearable={true}
                isSearchable={true}
                noOptionsMessage={() => "No clients found"}
              />
            </div>
          </div>
          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
              ></button>
            </div>
          )}
          {currentLocations.length === 0 && !loading ? (
            <TableDataNoAvailable message="No locations found." />
          ) : (
            <div className="position-relative">
              <div
                className="table-responsive"
                style={{
                  opacity: loading ? 0.4 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
              >
                <table className="table bordered-table mb-0">
                  <thead>
                    <tr style={{ backgroundColor: "#475EAB", color: "white" }}>
                      <th scope="col" className="p-2">
                        S.No
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("location_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Location Name</span>
                          <SortIcons
                            field="location_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("location_code")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Location Code</span>
                          <SortIcons
                            field="location_code"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("notes")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Notes</span>
                          <SortIcons field="notes" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("branch_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Branch Name</span>
                          <SortIcons
                            field="branch_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("company_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Company Name</span>
                          <SortIcons
                            field="company_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("client_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Client Name</span>
                          <SortIcons
                            field="client_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("is_active")}
                        className="p-2 text-center"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex">
                          <span>Status</span>
                          <SortIcons
                            field="is_active"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("created_datetime")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created Date</span>
                          <SortIcons
                            field="created_datetime"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentLocations.map((location, index) => (
                      <tr key={location.location_id || index}>
                        <td className="p-2">{startIndex + index + 1}</td>
                        <td className="p-2">
                          <TextWrap
                            location={location.location_name || "-"}
                          />
                        </td>
                        <td className="p-2">
                          {location.location_code || "-"}
                        </td>
                        <td className="p-2">
                          <TextWrap location={location.notes || "-"} />
                        </td>
                        <td className="p-2">
                          <TextWrap location={location.branch_name || "-"} />
                        </td>
                        <td className="p-2">
                          <TextWrap location={location.company_name || "-"} />
                        </td>
                        <td className="p-2">
                          <TextWrap location={location.client_name || "-"} />
                        </td>
                        <td className="p-2">
                          <span
                            className={`px-3 py-1 radius-4 fw-medium text-sm ${
                              location.is_active
                                ? "bg-success-focus text-success-600 border border-success-main"
                                : "bg-danger-focus text-danger-600 border border-danger-main"
                            }`}
                          >
                            {location.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="p-2">
                          {location.created_datetime || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {loading && (
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                  style={{
                    zIndex: 10,
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <LoadingComponent />
                </div>
              )}
              <PaginationControls
                pagination={{
                  currentPage,
                  perPage: entriesPerPage,
                  total: totalEntries,
                }}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BranchLocations;
